import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Building2, ChevronRight, LogIn, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { SettingsBackLink } from "@/components/settings/SettingsBackLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { logSuperadminAction } from "@/lib/superadminAudit";
import { useAuth } from "@/contexts/AuthContext";
import { DeletedWorkOrders } from "@/components/admin/DeletedWorkOrders";

type Company = { id: string; name: string; created_at: string };

export default function CompanyDetail() {
  const { companyId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [counts, setCounts] = useState({ members: 0, admins: 0 });
  const [loading, setLoading] = useState(true);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setCompany(null);
    if (!companyId) { setLoading(false); return () => { active = false; }; }
    Promise.all([
      supabase.from("companies").select("id, name, created_at").eq("id", companyId).maybeSingle(),
      supabase.from("user_companies").select("role").eq("company_id", companyId),
    ]).then(([result, memberships]) => {
      if (!active) return;
      setCompany(result.data);
      setCounts({ members: memberships.data?.length ?? 0, admins: memberships.data?.filter((m) => m.role === "admin").length ?? 0 });
      setLoading(false);
    });
    return () => { active = false; };
  }, [companyId]);

  if (loading) return <div className="p-6 text-muted-foreground">Loading company…</div>;
  if (!company) return <div className="p-6"><h1 className="text-2xl font-bold">Company not found</h1><p className="mt-2 text-muted-foreground">It may have been deleted or the link is incorrect.</p><SettingsBackLink className="mt-4" to="/platform/companies" label="Back to companies" /></div>;

  const rename = async () => {
    if (!company || !renameValue.trim() || busy) return;
    setBusy(true);
    const oldName = company.name;
    const name = renameValue.trim();
    const { error } = await supabase.from("companies").update({ name }).eq("id", company.id);
    if (error) toast.error(error.message);
    else {
      setCompany({ ...company, name });
      setRenameOpen(false);
      await logSuperadminAction({ action: "company_renamed", targetCompany: { id: company.id, name }, metadata: { old_name: oldName, new_name: name } });
      toast.success("Company renamed");
    }
    setBusy(false);
  };
  const remove = async () => {
    if (!company || deleteConfirm !== company.name || busy) return;
    setBusy(true);
    const { error } = await supabase.from("companies").delete().eq("id", company.id);
    if (error) { toast.error(error.message); setBusy(false); return; }
    await logSuperadminAction({ action: "company_deleted", targetCompany: { id: company.id, name: company.name } });
    toast.success("Company deleted");
    navigate("/platform/companies", { replace: true });
  };
  const openAs = async () => {
    if (!company) return;
    try {
      if (user) {
        await supabase.from("user_companies").upsert({ user_id: user.id, company_id: company.id }, { onConflict: "user_id,company_id" });
      }
      const { error } = await supabase.rpc("set_active_company", { _company_id: company.id });
      if (error) throw error;
      await logSuperadminAction({ action: "tenant_switched", targetCompany: { id: company.id, name: company.name } });
      window.location.href = "/";
    } catch (error) { toast.error(error instanceof Error ? error.message : "Failed to switch company"); }
  };

  const sections = [
    { path: "users", title: "Users & roles", description: "Members, invitations and company roles" },
    { path: "features", title: "App Features", description: "Choose the features available to this company" },
    { path: "workflows", title: "Workflow Availability", description: "Enable workflow types for this company" },
  ];
  const base = `/platform/companies/${company.id}`;
  return (
    <div className="min-h-screen bg-background pb-24 px-4 sm:px-6 pt-6">
      <SettingsBackLink to="/platform/companies" label="Companies" />
      <div className="flex items-center gap-2 min-w-0">
        <Building2 className="w-6 h-6 text-primary shrink-0" />
        <h1 className="text-2xl font-bold truncate">{company.name}</h1>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{counts.members} members · {counts.admins} company admins · Created {new Date(company.created_at).toLocaleDateString()}</p>
      <nav aria-label="Company settings" className="mt-6 flex gap-1 border-b overflow-x-auto whitespace-nowrap min-w-0">
        <NavLink end to={base} className={({ isActive }) => `shrink-0 px-3 py-2 text-sm border-b-2 ${isActive ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>Overview</NavLink>
        {sections.map((s) => <NavLink key={s.path} to={`${base}/${s.path}`} className={({ isActive }) => `shrink-0 px-3 py-2 text-sm border-b-2 ${isActive ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{s.title}</NavLink>)}
      </nav>
      <Outlet context={company} />
      {pathname === base && (
        <div className="mt-6 divide-y border-y">
          {sections.map((s) => <Link key={s.path} to={`${base}/${s.path}`} className="flex items-center justify-between gap-3 py-5 hover:bg-muted/30 transition-colors">
            <span><span className="font-medium block">{s.title}</span><span className="text-sm text-muted-foreground">{s.description}</span></span><ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
          </Link>)}
          <Link to="/platform/workflows" className="flex items-center justify-between gap-3 py-5 hover:bg-muted/30 transition-colors"><span><span className="font-medium block">Global workflow editor</span><span className="text-sm text-muted-foreground">Edit shared workflow definitions for the platform</span></span><ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" /></Link>
        </div>
      )}
      {pathname === base && <DeletedWorkOrders companyId={company.id} />}
      {pathname === base && <section className="mt-10 border-t pt-6">
        <h2 className="text-lg font-semibold">Company management</h2>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button variant="outline" onClick={() => { setRenameValue(company.name); setRenameOpen(true); }}><Pencil className="w-4 h-4 mr-2" />Rename</Button>
          <Button variant="outline" onClick={openAs}><LogIn className="w-4 h-4 mr-2" />Open as</Button>
          <Button variant="outline" className="text-destructive" onClick={() => { setDeleteConfirm(""); setDeleteOpen(true); }}><Trash2 className="w-4 h-4 mr-2" />Delete</Button>
        </div>
      </section>}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}><DialogContent><DialogHeader><DialogTitle>Rename {company.name}</DialogTitle></DialogHeader><Input aria-label="Company name" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} /><DialogFooter><Button variant="outline" onClick={() => setRenameOpen(false)}>Cancel</Button><Button disabled={busy || !renameValue.trim() || renameValue.trim() === company.name} onClick={rename}>Save</Button></DialogFooter></DialogContent></Dialog>
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}><DialogContent><DialogHeader><DialogTitle>Delete {company.name}?</DialogTitle></DialogHeader><p className="text-sm text-muted-foreground">This permanently deletes the company and unlinks its members. Tenant data may become inaccessible. Type its name to confirm.</p><Input aria-label="Type company name to confirm" placeholder={company.name} value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} /><DialogFooter><Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button><Button variant="destructive" disabled={busy || deleteConfirm !== company.name} onClick={remove}>Delete permanently</Button></DialogFooter></DialogContent></Dialog>
    </div>
  );
}